import {useQuery} from '@apollo/client'
import {Box, Button, Flex, FormLabel, Link, SimpleGrid, Text,} from '@chakra-ui/react'
import {yupResolver} from '@hookform/resolvers/yup'
import {Trans, useTranslation} from 'next-i18next'
import {FC, useEffect, useMemo, useState} from 'react'
import {FormProvider, useForm} from 'react-hook-form'

import Checkbox from '_app/components/Form/Checkbox'
import Input from '_app/components/Form/Input'
import {GET_COUNTRIES} from '_app/graphql/queries/customer/GetCountries'
import RegionsSelect from 'modules/checkout/components/RegionsSelect'
import PlacesAutocomplete from '_app/components/Form/PlacesAutocomplete'
import {Country} from '_app/graphql/generated/graphql'
import {useAddressFormErrors, useCheckoutContext,} from 'modules/checkout/hooks'
import useCustomer from '_app/hooks/useCustomer'
import {getCountryById} from 'modules/checkout/utils/regions'
import {LinkText} from '_app/components'
import {APP_ROUTES} from '_app/routes'
import PhoneInput from '_app/components/Form/PhoneInput'
import {INPOST_METHOD} from 'modules/checkout/constants'
import BillingAddressForm from '../BillingAddressForm'
import useAddressForm from './useAddressForm'
import {parseShowroomAddress} from './utils'
import {getDefaultValues} from './defaultValues'
import {addressSchema} from './validation'
import {useFormValues} from './useFormValues'
import {BUILDING_NUMBER_REGEX} from './constants'
import {useRouter} from '../../../../_app/hooks/useRouter'
import EventsHelper from '../../../../helpers/EventsHelper/EventsHelper'
import toast from '../../../../_app/utils/toast'

type AddressFormProps = {
  onOpen: () => void;
  shippingCode?: string;
  isAddressHidden?: boolean;
};

const AddressForm: FC<AddressFormProps> = ({
                                             onOpen,
                                             shippingCode,
                                             isAddressHidden,
                                           }) => {
  const [isChecked, setIsChecked] = useState<any>()
  const { customer } = useCustomer()
  const { locale } = useRouter()
  const { t } = useTranslation('checkout')

  const { data: countriesData } =
    useQuery<{ countries: Country[] }>(GET_COUNTRIES)

  const {
    inpostAddressDetails,
    selectedShowroom,
    inpostAddress,
    cartCheckoutData: data,
    areTermsAccepted,
  } = useCheckoutContext()

  const { isInpostOrShowroom, isInpost, isFakeAddress, shippingMethod } =
    useFormValues(data)

  const handleInpostShowroomAddress = () => {
    if (!isInpostOrShowroom) return {}
    const addressDetails: Record<string, string | string[]> = {}
    if (shippingMethod?.method_code === INPOST_METHOD && inpostAddressDetails) {
      const { street, building_number, flat_number, post_code, city } =
        inpostAddressDetails
      const parsedBuildingNumber = BUILDING_NUMBER_REGEX.test(building_number) ? building_number : 1
      addressDetails.street = [street, `${parsedBuildingNumber}${!!flat_number ? `/${flat_number}` : ''}`]
      addressDetails.postcode = post_code
      addressDetails.city = city
      return addressDetails
    }
    if (shippingMethod?.method_code === 'showroom' && selectedShowroom) return parseShowroomAddress(selectedShowroom)
    return {}
  }

  const defaultValues = getDefaultValues({
    data,
    isFakeAddress,
    customer,
    handleInpostShowroomAddress,
    areTermsAccepted,
    countries: countriesData?.countries,
    isInpostOrShowroom,
  })

  const selectedCountry = getCountryById(
    shippingCode,
    countriesData?.countries,
  )

  const regionsAvailable = useMemo(
    () =>
      !!selectedCountry?.available_regions?.length &&
      selectedCountry?.id !== 'PL',
    [selectedCountry],
  )
  const methods = useForm({
    defaultValues,
    resolver: yupResolver(addressSchema),
    context: { customer, isInpostOrShowroom, isInpost, regionsAvailable },
    mode: 'onBlur',
  })

  const { handleSubmit, reset, setValue, formState, watch } = methods

  useAddressFormErrors(formState)

  const { onSubmit } = useAddressForm({
    setIsChecked,
    regionsAvailable,
    locale,
    t,
  })

  useEffect(() => {
    if (data) {
      reset(defaultValues)
    }
  }, [inpostAddressDetails, selectedShowroom, shippingCode, data, customer])

  useEffect(() => {
    if (
      Object.keys(formState.errors).filter(key => key !== 'accept_regulations')
        .length === 0 &&
      formState.errors?.accept_regulations?.type === 'accept_regulations'
    ) {
      EventsHelper.scrollToElement('accept_regulations')
    }
  }, [formState.errors])

  const receiveInvoiceWatch = watch('receive_invoice')

  const scrollTopTop = () => {
    document.body.scrollTop = 0
    document.documentElement.scrollTop = 0
  }

  const handleCheck = () => {
    if (!selectedShowroom && data?.cart?.shipping_addresses?.[0]?.selected_shipping_method?.method_code === 'showroom') {
      return false
    }
    if (!inpostAddress && data?.cart?.shipping_addresses?.[0]?.selected_shipping_method?.carrier_code === 'inpostlocker') {
      return false
    }
    return true
  }

  const handleCheckAndToast = () => {
    if (!selectedShowroom && data?.cart?.shipping_addresses?.[0]?.selected_shipping_method?.method_code === 'showroom') {
      toast({ description: 'Proszę o wybranie punktu odbioru paczki.', status: 'error' })
      scrollTopTop()
      return false
    }
    if (!inpostAddress && data?.cart?.shipping_addresses?.[0]?.selected_shipping_method?.carrier_code === 'inpostlocker') {
      toast({ description: 'Proszę o wybranie punktu odbioru paczki.', status: 'error' })
      scrollTopTop()
      return false
    }
    return true
  }

  useEffect(() => {
    if (isChecked !== undefined) {
      Object.entries(isChecked).forEach(([name, value]) =>
        setValue(name, value),
      )
    }
  }, [isChecked])

  useEffect(() => {
    if (receiveInvoiceWatch) {
      localStorage?.setItem('receiveInvoiceWatch', 'true')
      setValue('billing_address.city', '')
      setValue('billing_address.postcode', '')
      setValue('billing_address.street[0]', '')
      setValue('billing_address.street[1]', '')
    } else localStorage?.setItem('receiveInvoiceWatch', 'false')
  }, [receiveInvoiceWatch])
  const handleSubmitExternal = (e: any) => {
    e.preventDefault()
    const check = handleCheck()
    if (check) {
      const tempData = methods.getValues()
      if (tempData) {
        handleSubmit((data) => onSubmit(data))(e)
      }
    }
  }
  return (
    <>
      <Box mt={10}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmitExternal} id="checkout[1]">
            {!!customer && !isAddressHidden && (
              <Box mb={4}>
                <Link onClick={onOpen} fontWeight={300}>
                  {t('CHOOSE_FROM_SAVED_ADDRESSES')}
                </Link>
              </Box>
            )}
            <Text as={FormLabel} mb={4}>
              {t('CONTACT_DETAILS')}
            </Text>
            <SimpleGrid columns={2} spacingX={{ base: 4, lg: 8 }}>
              <Input
                isRequired
                label={t('FIRST_NAME_LABEL')}
                name="shipping_address.firstname"
                placeholder={t('FIRST_NAME_PLACEHOLDER')}
              />
              <Input
                isRequired
                label={t('LAST_NAME_LABEL')}
                name="shipping_address.lastname"
                placeholder={t('LAST_NAME_PLACEHOLDER')}
              />
            </SimpleGrid>
            <SimpleGrid
              mb={isAddressHidden ? 6 : 0}
              columns={{ base: 1, md: 2 }}
              spacingX={{ base: 4, lg: 8 }}
            >
              <Input
                type="email"
                isRequired
                label={t('EMAIL_ADDRESS_LABEL')}
                name="email"
                placeholder={t('EMAIL_ADDRESS_PLACEHOLDER')}
              />

              <PhoneInput
                isRequired
                label={t('PHONE_NUMBER_LABEL')}
                name="shipping_address.telephone"
                defaultValue={defaultValues?.shipping_address?.telephone}
                placeholder={t('PHONE_NUMBER_PLACEHOLDER')}
              />
            </SimpleGrid>

            <Box display={isAddressHidden ? 'none' : 'block'}>
              <Text as={FormLabel} my={10} mb={4}>
                {t('DELIVERY_ADDRESS')}
              </Text>
              <Flex gap={{ base: 4, lg: 8 }}>
                <Flex width="100%">
                  <PlacesAutocomplete
                    name="shipping_address.street[0]"
                    defaultValue={defaultValues?.shipping_address?.street?.[0]}
                    streetNumberKey="shipping_address.street[1]"
                    countryKey="shipping_address.country_code"
                    postcodeKey="shipping_address.postcode"
                    cityKey="shipping_address.city"
                    regionKey="shipping_address.region_id"
                  />
                </Flex>
              </Flex>
              <SimpleGrid columns={2} spacingX={{ base: 4, lg: 8 }}>
                <Input
                  isRequired
                  label={t('HOUSE_NUMBER_LABEL')}
                  name="shipping_address.street[1]"
                  placeholder={t('HOUSE_NUMBER_PLACEHOLDER')}
                  maxLength={7}
                />
                <Input
                  label={t('APARTMENT_NUMBER_LABEL')}
                  name="shipping_address.street[2]"
                  placeholder={t('APARTMENT_NUMBER_PLACEHOLDER')}
                  maxLength={7}
                />
              </SimpleGrid>
              <SimpleGrid columns={2} spacingX={{ base: 4, lg: 8 }}>
                <Input
                  label={t('CITY_LABEL')}
                  name="shipping_address.city"
                  placeholder={t('CITY_PLACEHOLDER')}
                />
                <Input
                  label={t('POSTCODE_LABEL')}
                  name="shipping_address.postcode"
                  placeholder={t('POSTCODE_PLACEHOLDER')}
                />
              </SimpleGrid>

              <Input
                mb={6}
                label={t('COMPANY_NAME_FLOOR_NUMBER_LABEL')}
                name="shipping_address.company"
                placeholder={t('COMPANY_NAME_FLOOR_NUMBER_PLACEHOLDER')}
              />
            </Box>

            {regionsAvailable && (
              <RegionsSelect
                country={selectedCountry}
                name="shipping_address.region_id"
              />
            )}
            <Box textStyle="bodyLight" id="AddressCheckboxes">
              {
                selectedCountry && (
                  <Checkbox
                    isDisabled={selectedCountry?.id !== 'PL' || locale !== 'pl'}
                    name="receive_invoice"
                    label={t('I_WANT_INVOICE')}
                  />
                )
              }
              {receiveInvoiceWatch && (
                <BillingAddressForm
                  countries={countriesData?.countries || []}
                />
              )}
              {/* <Checkbox*/}
              {/*    sx={{*/}
              {/*      display: 'none',*/}
              {/*    }}*/}
              {/*  id="newsletter_checkbox"*/}
              {/*  name="subscribe_to_newsletter_email"*/}
              {/*  label={*/}
              {/*    <Trans*/}
              {/*      i18nKey="I_WANT_SIGN_UP_TO_NEWSLETTER"*/}
              {/*      ns="checkout"*/}
              {/*      components={{*/}
              {/*        link1: (*/}
              {/*          <LinkText href={APP_ROUTES.NEWSLETTER_REGULATIONS} />*/}
              {/*        ),*/}
              {/*      }}*/}
              {/*    />*/}
              {/*  }*/}
              {/* />*/}
              {!customer && (
                <Box id="accept_regulations">
                  <Checkbox
                    isRequired
                    name="accept_regulations"
                    label={
                      <Trans
                        i18nKey="ACCEPT_CONDITIONS"
                        ns="common"
                        components={{
                          link1: (
                            <LinkText href={APP_ROUTES.TERMS_AND_CONDITIONS} />
                          ),
                          link2: <LinkText href={APP_ROUTES.PRIVACY_POLICY} />,
                        }}
                      />
                    }
                  />
                </Box>
              )}

              <Text mt={2} textStyle="body2">
                {t('REQUIRED_FIELDS', { ns: 'common' })}
              </Text>
            </Box>
          </form>
        </FormProvider>
      </Box>

      <Box borderTop="1px solid #ddd" pt={6} mt={6}>
        <Button
          width={{ base: 'full', md: '20.625rem' }}
          type="submit"
          form="checkout[1]"
          onClick={() => handleCheckAndToast()}
          isLoading={formState.isSubmitting}
        >
          {t('SELECT_PAYMENT_METHOD')}
        </Button>
      </Box>
    </>
  )
}

export default AddressForm
